import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { CalendarDays, ChevronDown, X } from 'lucide-react';
export const ALL_TIME = { from: '', to: '', preset: 'all' };
const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    d.setUTCDate(d.getUTCDate() - n);
    return d;
};
/** Tomorrow, so "today" is fully inside a range whose end is exclusive. */
const exclusiveToday = () => iso(daysAgo(-1));
const utcDate = (y, m, d = 1) => new Date(Date.UTC(y, m, d));
export const PRESETS = [
    {
        key: 'today',
        label: 'Today',
        build: () => ({ from: iso(daysAgo(0)), to: exclusiveToday(), preset: 'today' }),
    },
    {
        key: 'yesterday',
        label: 'Yesterday',
        build: () => ({ from: iso(daysAgo(1)), to: iso(daysAgo(0)), preset: 'yesterday' }),
    },
    {
        key: '7d',
        label: 'Last 7 days',
        build: () => ({ from: iso(daysAgo(6)), to: exclusiveToday(), preset: '7d' }),
    },
    {
        key: '30d',
        label: 'Last 30 days',
        build: () => ({ from: iso(daysAgo(29)), to: exclusiveToday(), preset: '30d' }),
    },
    {
        key: 'mtd',
        label: 'This month',
        build: () => {
            const d = new Date();
            return {
                from: iso(utcDate(d.getUTCFullYear(), d.getUTCMonth())),
                to: exclusiveToday(),
                preset: 'mtd',
            };
        },
    },
    {
        key: 'last_month',
        label: 'Last month',
        build: () => {
            const d = new Date();
            return {
                from: iso(utcDate(d.getUTCFullYear(), d.getUTCMonth() - 1)),
                to: iso(utcDate(d.getUTCFullYear(), d.getUTCMonth())),
                preset: 'last_month',
            };
        },
    },
    {
        key: '90d',
        label: 'Last 3 months',
        build: () => ({ from: iso(daysAgo(89)), to: exclusiveToday(), preset: '90d' }),
    },
    {
        key: '6m',
        label: 'Last 6 months',
        build: () => ({ from: iso(daysAgo(182)), to: exclusiveToday(), preset: '6m' }),
    },
    {
        key: 'ytd',
        label: 'This year',
        build: () => ({
            from: iso(utcDate(new Date().getUTCFullYear(), 0)),
            to: exclusiveToday(),
            preset: 'ytd',
        }),
    },
    {
        key: 'last_year',
        label: 'Last year',
        build: () => {
            const y = new Date().getUTCFullYear();
            return { from: iso(utcDate(y - 1, 0)), to: iso(utcDate(y, 0)), preset: 'last_year' };
        },
    },
    {
        key: '12m',
        label: 'Last 12 months',
        build: () => ({ from: iso(daysAgo(364)), to: exclusiveToday(), preset: '12m' }),
    },
    {
        key: 'all',
        label: 'All time',
        // No bounds at all — see the note above on why an invented start date is
        // worse than none.
        build: () => ALL_TIME,
    },
];
/** Human summary of the active range, for the trigger and the "showing" line. */
export const describeRange = (range) => {
    const preset = PRESETS.find((p) => p.key === range.preset);
    if (preset)
        return preset.label;
    if (!range.from && !range.to)
        return 'All time';
    const pretty = (v) => v ? new Date(`${v}T00:00:00Z`).toLocaleDateString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric',
    }) : '…';
    // Rendered back as INCLUSIVE, because that is what the operator picked; the
    // exclusive end is a wire detail and showing it would read as an off-by-one.
    const inclusiveEnd = range.to
        ? iso(new Date(new Date(`${range.to}T00:00:00Z`).getTime() - 86400000))
        : '';
    return `${pretty(range.from)} – ${pretty(inclusiveEnd)}`;
};
export const DateRangeFilter = ({ value, onChange, className = '', compact = false }) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({ from: '', to: '' });
    const rootRef = React.useRef(null);
    React.useEffect(() => {
        if (!open)
            return;
        const onDown = (e) => {
            var _a;
            if (!((_a = rootRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)))
                setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);
    React.useEffect(() => {
        if (!open)
            return;
        // Seed the custom fields from whatever is active, shown inclusively so the
        // dates in the inputs match the dates on the label.
        setDraft({
            from: value.from,
            to: value.to
                ? iso(new Date(new Date(`${value.to}T00:00:00Z`).getTime() - 86400000))
                : '',
        });
    }, [open, value]);
    const label = useMemo(() => describeRange(value), [value]);
    const isCustom = value.preset === 'custom';
    const applyCustom = () => {
        if (!draft.from && !draft.to)
            return;
        onChange({
            from: draft.from,
            // Inclusive in the UI, exclusive on the wire — the +1 day that keeps the
            // last selected day inside the range.
            to: draft.to
                ? iso(new Date(new Date(`${draft.to}T00:00:00Z`).getTime() + 86400000))
                : '',
            preset: 'custom',
        });
        setOpen(false);
    };
    return (_jsxs("div", { ref: rootRef, className: `relative flex min-w-0 flex-col gap-1 ${className}`, children: [!compact && (_jsx("span", { className: 'text-[11px] font-medium uppercase tracking-wide text-dash-muted', children: "Reporting period" })), _jsxs("button", { type: 'button', onClick: () => setOpen((v) => !v), "aria-haspopup": 'dialog', "aria-expanded": open, className: `flex h-9 max-aiMd:h-11 items-center justify-between gap-2 rounded-lg border bg-dash-surface px-2.5 text-[12.5px] text-dash-text outline-none transition-colors cursor-pointer ${open ? 'border-dash-accent' : 'border-dash-border hover:border-dash-borderStrong'}`, children: [_jsxs("span", { className: 'flex min-w-0 items-center gap-1.5', children: [_jsx(CalendarDays, { className: 'h-3.5 w-3.5 flex-shrink-0 text-dash-muted' }), _jsx("span", { className: 'truncate', children: label })] }), _jsx(ChevronDown, { className: `h-3.5 w-3.5 flex-shrink-0 text-dash-muted transition-transform ${open ? 'rotate-180' : ''}` })] }), !compact && (_jsx("span", { className: 'text-[10.5px] leading-snug text-dash-muted', children: isCustom ? 'Custom range' : 'Window the report covers' })), open && (_jsxs("div", { role: 'dialog', "aria-label": 'Select reporting period', className: `absolute left-0 z-30 w-[min(320px,90vw)] overflow-hidden rounded-lg border border-dash-border bg-dash-bg2 shadow-xl ${compact ? 'top-full mt-1' : 'top-[58px]'}`, children: [_jsx("div", { className: 'grid grid-cols-2 gap-0.5 p-1', children: PRESETS.map((p) => {
                            const active = value.preset === p.key;
                            return (_jsx("button", { type: 'button', onClick: () => {
                                    onChange(p.build());
                                    setOpen(false);
                                }, className: `flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-[12px] transition-colors cursor-pointer ${active
                                    ? 'bg-dash-accentSoft font-semibold text-dash-text'
                                    : 'text-dash-text2 hover:bg-dash-hover'}`, children: p.label }, p.key));
                        }) }), _jsxs("div", { className: 'border-t border-dash-border p-2.5', children: [_jsx("p", { className: 'mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-dash-muted', children: "Custom range" }), _jsxs("div", { className: 'flex items-end gap-1.5', children: [_jsxs("label", { className: 'flex min-w-0 flex-1 flex-col gap-0.5', children: [_jsx("span", { className: 'text-[10px] text-dash-muted', children: "From" }), _jsx("input", { type: 'date', value: draft.from, max: draft.to || undefined, onChange: (e) => setDraft((d) => (Object.assign(Object.assign({}, d), { from: e.target.value }))), className: 'h-8 w-full rounded-md border border-dash-border bg-dash-surface px-1.5 text-[11.5px] text-dash-text outline-none focus:border-dash-accent' })] }), _jsxs("label", { className: 'flex min-w-0 flex-1 flex-col gap-0.5', children: [_jsx("span", { className: 'text-[10px] text-dash-muted', children: "To" }), _jsx("input", { type: 'date', value: draft.to, min: draft.from || undefined, onChange: (e) => setDraft((d) => (Object.assign(Object.assign({}, d), { to: e.target.value }))), className: 'h-8 w-full rounded-md border border-dash-border bg-dash-surface px-1.5 text-[11.5px] text-dash-text outline-none focus:border-dash-accent' })] })] }), _jsx("p", { className: 'mt-1 text-[10px] text-dash-muted', children: "Both dates are included in the report." }), _jsxs("div", { className: 'mt-2 flex items-center gap-1.5', children: [_jsx("button", { type: 'button', onClick: applyCustom, disabled: !draft.from && !draft.to, className: `flex-1 rounded-md px-2 py-1.5 text-[11.5px] font-medium transition-colors ${!draft.from && !draft.to
                                            ? 'cursor-not-allowed bg-dash-active text-dash-muted'
                                            : 'cursor-pointer bg-dash-accent text-white hover:bg-dash-accentHover'}`, children: "Apply" }), (value.from || value.to) && (_jsxs("button", { type: 'button', onClick: () => {
                                            onChange(ALL_TIME);
                                            setOpen(false);
                                        }, className: 'inline-flex items-center gap-1 rounded-md border border-dash-border px-2 py-1.5 text-[11.5px] font-medium text-dash-text2 transition-colors hover:bg-dash-hover cursor-pointer', children: [_jsx(X, { className: 'h-3 w-3' }), "Clear"] }))] })] })] }))] }));
};
export default DateRangeFilter;
